import React from 'react';
import { Container, Row, Col, Card, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/common/SEO';
import './Quality.css';

const Quality: React.FC = () => {
  const qualityStandards = [
    {
      title: 'ISO 9001:2015',
      description: 'Quality Management System certification ensuring consistent product quality and customer satisfaction.',
      icon: '🏆'
    },
    {
      title: 'FSC Certified',
      description: 'Responsible forestry practices ensuring sustainable sourcing of raw materials.',
      icon: '🌳'
    },
    {
      title: 'Food Safe',
      description: 'Materials certified safe for contact with food products, meeting international standards.',
      icon: '🍽️'
    },
    {
      title: 'Quality Testing',
      description: 'Rigorous multi-stage testing protocols for all products before dispatch.',
      icon: '🔬'
    }
  ];

  const qualityParameters = [
    {
      parameter: 'GSM (Grams per Square Meter)',
      range: '13-17 GSM',
      purpose: 'Determines paper weight and thickness for optimal performance'
    },
    {
      parameter: 'Ply Count',
      range: '1-2 Ply',
      purpose: 'Multiple layers for enhanced softness and strength'
    },
    {
      parameter: 'Tensile Strength',
      range: 'ISO Standards',
      purpose: 'Ensures paper doesn\'t tear easily during use'
    },
    {
      parameter: 'Absorbency',
      range: 'High Grade',
      purpose: 'Quick liquid absorption for effective cleaning'
    },
    {
      parameter: 'Softness',
      range: 'Premium',
      purpose: 'Gentle on skin, comfortable for daily use'
    },
    {
      parameter: 'Brightness',
      range: '85-90%',
      purpose: 'Visual appeal and product presentation'
    }
  ];

  const manufacturingProcess = [
    {
      step: 1,
      title: 'Raw Material Selection',
      description: 'Careful selection of virgin and recycled pulp from certified suppliers',
      icon: '📦'
    },
    {
      step: 2,
      title: 'Pulp Processing',
      description: 'Advanced processing to achieve desired fiber characteristics',
      icon: '⚙️'
    },
    {
      step: 3,
      title: 'Paper Formation',
      description: 'State-of-the-art machinery for consistent paper formation',
      icon: '🏭'
    },
    {
      step: 4,
      title: 'Quality Testing',
      description: 'Multi-point quality checks at every production stage',
      icon: '✓'
    },
    {
      step: 5,
      title: 'Converting & Cutting',
      description: 'Precision cutting and folding to final product specifications',
      icon: '✂️'
    },
    {
      step: 6,
      title: 'Packaging & Dispatch',
      description: 'Hygienic packaging and quality-controlled dispatch',
      icon: '📮'
    }
  ];

  return (
    <>
      <SEO
        title="Quality Standards & Certifications"
        description="Learn about Skytion Enterprises' commitment to quality. ISO 9001:2015 certified manufacturing, FSC certification, and rigorous quality testing for all tissue products."
        keywords="ISO certification, quality standards, FSC certified, tissue quality, manufacturing standards, quality control"
      />

      {/* Hero Section */}
      <section className="quality-hero py-5">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto quality-hero-content">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Badge bg="light" text="dark" className="mb-3 px-4 py-2">Quality Assurance</Badge>
                <h1 className="display-2 mb-4">Committed to Excellence</h1>
                <p className="lead mb-4">
                  At Skytion Enterprises, quality isn't just a goal—it's our foundation.
                  Every product undergoes rigorous testing to meet international standards.
                </p>
                <div className="d-flex gap-2 justify-content-center flex-wrap">
                  <Badge bg="light" text="dark" className="px-3 py-2 fs-6">ISO 9001:2015</Badge>
                  <Badge bg="light" text="dark" className="px-3 py-2 fs-6">FSC Certified</Badge>
                  <Badge bg="light" text="dark" className="px-3 py-2 fs-6">Food Safe</Badge>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Quality Standards */}
      <section className="py-5">
        <Container>
          <Row className="mb-5">
            <Col lg={8} className="mx-auto text-center">
              <h2 className="display-5 mb-3">Our Quality Standards</h2>
              <p className="lead">
                We maintain the highest quality standards across all our brands and product lines
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            {qualityStandards.map((standard, index) => (
              <Col lg={3} md={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="quality-standard-card">
                    <Card.Body>
                      <div className="quality-standard-icon">
                        {standard.icon}
                      </div>
                      <h4 className="mb-3">{standard.title}</h4>
                      <p className="text-muted">{standard.description}</p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Quality Parameters */}
      <section className="quality-parameters-section py-5">
        <Container>
          <Row className="mb-5">
            <Col lg={8} className="mx-auto text-center">
              <h2 className="display-5 mb-3">Quality Parameters We Test</h2>
              <p className="lead">
                Every product is tested against multiple quality parameters
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            {qualityParameters.map((param, index) => (
              <Col lg={4} md={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <Card className="quality-parameter-card">
                    <Card.Body>
                      <div className="parameter-header">
                        <h5 className="mb-0">{param.parameter}</h5>
                        <Badge className="parameter-badge">{param.range}</Badge>
                      </div>
                      <p className="text-muted mb-0">{param.purpose}</p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Manufacturing Process */}
      <section className="py-5">
        <Container>
          <Row className="mb-5">
            <Col lg={8} className="mx-auto text-center">
              <h2 className="display-5 mb-3">Quality-Controlled Manufacturing</h2>
              <p className="lead">
                Our 6-step process ensures consistent quality at every stage
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            {manufacturingProcess.map((process, index) => (
              <Col lg={4} md={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="process-card">
                    <Card.Body>
                      <div className="process-header">
                        <div className="process-step-badge">
                          {process.step}
                        </div>
                        <div className="process-icon-badge">
                          {process.icon}
                        </div>
                      </div>
                      <h5 className="mb-2">{process.title}</h5>
                      <p className="text-muted mb-0">{process.description}</p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Commitment Section */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <h2 className="display-5 mb-4">Our Quality Commitment</h2>
              <p className="lead mb-4">
                Quality assurance is embedded in everything we do, from sourcing raw materials
                to final product delivery.
              </p>
              <ul className="quality-commitment-list">
                <li>
                  <span className="commitment-check-icon">✓</span>
                  <strong>100% Quality Tested Products</strong> - Every batch undergoes comprehensive testing
                </li>
                <li>
                  <span className="commitment-check-icon">✓</span>
                  <strong>Certified Manufacturing Facility</strong> - ISO 9001:2015 certified operations
                </li>
                <li>
                  <span className="commitment-check-icon">✓</span>
                  <strong>Sustainable Sourcing</strong> - FSC certified raw materials
                </li>
                <li>
                  <span className="commitment-check-icon">✓</span>
                  <strong>Continuous Improvement</strong> - Regular process audits and upgrades
                </li>
                <li>
                  <span className="commitment-check-icon">✓</span>
                  <strong>Customer Satisfaction</strong> - 99.9% quality approval rating
                </li>
              </ul>
            </Col>
            <Col lg={6}>
              <Card className="quality-stats-card">
                <Card.Body>
                  <div className="text-center mb-4">
                    <div className="quality-percentage-badge">
                      99.9%
                    </div>
                    <h4>Quality Assurance Rate</h4>
                    <p className="text-muted">Based on customer feedback and internal testing</p>
                  </div>
                  <div className="quality-stats-grid">
                    <div className="quality-stat-item">
                      <h5>2022</h5>
                      <small>Est. Year</small>
                    </div>
                    <div className="quality-stat-item">
                      <h5>24/7</h5>
                      <small>QC Operations</small>
                    </div>
                    <div className="quality-stat-item">
                      <h5>3</h5>
                      <small>Countries</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-5">
        <Container>
          <Card className="quality-cta-card">
            <Card.Body className="quality-cta-content text-center text-white">
              <h3 className="mb-4">Experience Quality You Can Trust</h3>
              <p className="lead mb-4">
                Discover how our commitment to quality translates into superior tissue products
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link to="/brands" className="btn btn-light btn-lg">
                  Explore Our Brands
                </Link>
                <Link to="/contact" className="btn btn-outline-light btn-lg">
                  Request Quality Certificate
                </Link>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </section>
    </>
  );
};

export default Quality;
